import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { completeOnboarding, isOnboardingDone } from './onboardingStorage'
import './OnboardingTour.css'

const CARD_GAP = 14
const SPOT_PAD = 8

function availableSteps(steps) {
  return steps.filter((step) => !step.target || document.querySelector(step.target))
}

function measureTarget(selector) {
  if (!selector) return null
  const element = document.querySelector(selector)
  if (!element) return null
  const rect = element.getBoundingClientRect()
  if (rect.width < 2 && rect.height < 2) return null
  return {
    top: Math.max(8, rect.top - SPOT_PAD),
    left: Math.max(8, rect.left - SPOT_PAD),
    width: Math.min(rect.width + SPOT_PAD * 2, window.innerWidth - 16),
    height: rect.height + SPOT_PAD * 2,
    bottom: rect.bottom + SPOT_PAD,
  }
}

function placeCard(spot, cardHeight) {
  const width = Math.min(340, window.innerWidth - 24)
  const height = cardHeight || 220
  if (!spot) {
    return {
      top: Math.max(12, (window.innerHeight - height) / 2),
      left: Math.max(12, (window.innerWidth - width) / 2),
      width,
    }
  }
  let left = spot.left
  left = Math.max(12, Math.min(left, window.innerWidth - width - 12))
  const below = spot.bottom + CARD_GAP
  const above = spot.top - CARD_GAP - height
  let top = below
  if (below + height > window.innerHeight - 12 && above > 12) top = above
  if (top + height > window.innerHeight - 12) top = Math.max(12, window.innerHeight - height - 12)
  return { top, left, width }
}

function OnboardingTour({ tourId, steps, userId, ready = true, replayToken = 0 }) {
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [resolved, setResolved] = useState([])
  const [spot, setSpot] = useState(null)
  const [cardBox, setCardBox] = useState(null)
  const cardRef = useRef(null)
  const autoStarted = useRef(false)

  const close = useCallback((persist) => {
    if (persist) completeOnboarding(tourId, userId)
    setOpen(false)
  }, [tourId, userId])

  const begin = useCallback(() => {
    const next = availableSteps(steps)
    if (!next.length) return
    setResolved(next)
    setIndex(0)
    setOpen(true)
  }, [steps])

  useEffect(() => {
    if (!ready || autoStarted.current) return undefined
    autoStarted.current = true
    if (!isOnboardingDone(tourId, userId)) begin()
    return undefined
  }, [ready, tourId, userId, begin])

  useEffect(() => {
    if (!replayToken) return undefined
    begin()
    return undefined
  }, [replayToken, begin])

  const step = resolved[index]

  useLayoutEffect(() => {
    if (!open || !step) return undefined
    const target = step.target ? document.querySelector(step.target) : null
    if (target) {
      target.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      })
    }

    const update = () => {
      const nextSpot = measureTarget(step.target)
      const height = cardRef.current?.offsetHeight || 220
      setSpot(nextSpot)
      setCardBox(placeCard(nextSpot, height))
    }

    update()
    const timer = window.setTimeout(update, 360)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, step])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') close(true)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  if (!open || !step) return null

  const last = index === resolved.length - 1
  const goNext = () => {
    if (last) close(true)
    else setIndex((current) => current + 1)
  }

  return createPortal(
    <div className="onboarding-root" role="presentation">
      <div className={`onboarding-shield${spot ? '' : ' is-dim'}`} />
      {spot && (
        <div
          className="onboarding-spot"
          style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
        />
      )}
      <div
        ref={cardRef}
        className="onboarding-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        style={cardBox ? { top: cardBox.top, left: cardBox.left, width: cardBox.width } : undefined}
      >
        <p className="onboarding-kicker">Passo {index + 1} de {resolved.length}</p>
        <h2 id="onboarding-title">{step.title}</h2>
        <p>{step.body}</p>
        <div className="onboarding-actions">
          <button type="button" className="onboarding-skip" onClick={() => close(true)}>Pular</button>
          <div className="onboarding-nav">
            <button type="button" onClick={() => setIndex((current) => current - 1)} disabled={index === 0}>
              Voltar
            </button>
            <button type="button" className="onboarding-next" onClick={goNext} autoFocus>
              {last ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default OnboardingTour
